// Simple client-side translation helper with optional LibreTranslate backend
// If REACT_APP_TRANSLATE_URL is not set, try a best-effort fallback using Google public endpoint.

type TranslateResult = string;

const API_URL = (globalThis as any).process?.env?.REACT_APP_TRANSLATE_URL;

async function translateViaGoogle(text: string, targetLang: string, sourceLang?: string): Promise<string> {
  // Unofficial endpoint; may be rate-limited or blocked by CORS in some environments.
  try {
    const sl = sourceLang || 'auto';
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${encodeURIComponent(sl)}&tl=${encodeURIComponent(targetLang)}&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    if (!res.ok) return text;
    const data = await res.json();
    // Expected shape: [[ [translatedText, originalText, ...], ... ], ...]
    const translated = Array.isArray(data) && Array.isArray(data[0])
      ? data[0].map((seg: any) => (Array.isArray(seg) ? seg[0] : '')).join('')
      : undefined;
    return translated || text;
  } catch {
    return text;
  }
}

export async function translateText(text: string, targetLang: string, sourceLang?: string): Promise<TranslateResult> {
  if (!text || !targetLang) return text;
  // Try configured LibreTranslate-compatible API first
  if (API_URL) {
    try {
      const res = await fetch(`${API_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: text, source: sourceLang || 'auto', target: targetLang, format: 'text' })
      });
      if (res.ok) {
        const data = await res.json();
        // LibreTranslate returns { translatedText }
        if (data?.translatedText) return data.translatedText as string;
      }
    } catch {
      // fall through to google fallback
    }
  }
  // Fallback to Google public endpoint
  return translateViaGoogle(text, targetLang, sourceLang);
}

export async function translateTexts(texts: string[], targetLang: string, sourceLang?: string): Promise<string[]> {
  if (!texts || texts.length === 0) return texts;
  const results = await Promise.all(texts.map(t => translateText(t, targetLang, sourceLang)));
  return results;
}
